import { useTranslation } from 'next-i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { getUniqueOptions } from 'utils/general';

import { useGetUnitsList } from '@/modules/workforce/Contracts/apiHooks';
import CollapsedContractUnit from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractTypeInputs/UnitBase/Collapsed';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { InputField, SelectField, Typography } from '@/shared/components/atoms';
import EditableExpandableField from '@/shared/components/atoms/EditableExpandableField';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';
import { IGeneral } from '@/shared/types';

const UnitInput: React.FC<{
  index: number;
  handleAppend: IGeneral.IVoidFunction;
  handleRemove: IGeneral.IVoidFunction;
}> = ({ index, handleAppend, handleRemove }) => {
  const { t } = useTranslation();

  const {
    watch,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<IContracts.IAddContractSchema>();

  const entityWatcher = watch('entity');

  const unitBasedWatcher = watch('unitBased');
  const unitId = unitBasedWatcher?.[index]?.unitId;
  const unitName = unitBasedWatcher?.[index]?.unitName;
  const unitPrice = unitBasedWatcher?.[index]?.unitPrice;
  const unitEditable = unitBasedWatcher?.[index]?.unitEditable;
  const expanded = unitBasedWatcher?.[index]?.expanded;
  const entityCurrency = entityWatcher?.currency;
  const hasUnitMismatch = watch('hasUnitMismatch');

  const { data: unitsList } = useGetUnitsList(entityWatcher?.id);

  const uniqueOptions = unitsList
    ? getUniqueOptions<IContracts.IUnit>({
        allOptions: unitsList,
        selectedOptions: unitBasedWatcher?.map((unit) => ({
          name: unit.unitName as string,
          id: unit.unitId as number,
        })),
        getValue: (value) => value?.id,
        exclude: unitBasedWatcher?.[index]
          ? {
              name: unitBasedWatcher[index].unitName as string,
              id: unitBasedWatcher[index].unitId as number,
            }
          : undefined,
      })
    : undefined;

  const getUnitName = (id?: number): IContracts.IUnit | undefined => {
    return unitsList?.find((unit) => unit.id === id);
  };

  useDidUpdateEffect(() => {
    setValue(`unitBased.${index}.unitName`, getUnitName(unitId)?.name, {
      shouldValidate: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  return (
    <EditableExpandableField
      fieldArrayName='unitBased'
      addFieldText={t('add-unit')}
      editFieldText={t('edit-unit')}
      addAnotherFieldText={t('add-another-unit')}
      index={index}
      length={unitBasedWatcher?.length ?? 0}
      collapsedHeader={
        <div className='flex items-center gap-8'>
          <Typography variant='subtitle' weight='medium'>
            {unitName}
          </Typography>
        </div>
      }
      collapsedContent={
        <CollapsedContractUnit
          unitName={unitName as string}
          unitPrice={unitPrice as number}
          currency={entityCurrency}
        />
      }
      canDelete={unitEditable}
      actionsDisabled={hasUnitMismatch && !!unitPrice}
      expanded={expanded}
      actionsDisabledTooltipMessage={t('unit-uneditable')}
      handleAppend={handleAppend}
      handleRemove={handleRemove}
      actionsVariant={
        unitEditable && unitBasedWatcher?.length > 1 ? 'tooltip' : 'icons'
      }
    >
      <div className='flex flex-col gap-16'>
        <div className='relative flex items-center gap-24'>
          <div className='flex w-full flex-col gap-24'>
            <SelectField
              id={`unitBased.${index}.unitId`}
              isDisabled={!unitEditable}
              disabledMessage={t('unit-uneditable')}
              isSearchable
              borderColor='#B5BABF'
              label={t('unit')}
              getLabel={(item: IGeneral.NameIdPair) => item.name}
              getValue={(item: IGeneral.NameIdPair) => item.id}
              list={uniqueOptions}
              width='w-full'
              py='0.5rem'
              px='0.5rem'
              borderRadius='1rem'
              placeholder={t('unit')}
              className='rounded-xl'
            />
            <InputField
              id={`unitBased.${index}.unitPrice`}
              {...register(`unitBased.${index}.unitPrice`, {
                setValueAs: (value: string) =>
                  value ? Number(value) : undefined,
              })}
              className='rounded-xl !py-14'
              label={t('Unit Value')}
              placeholder={t('Unit Value')}
              width='w-full'
              error={!!errors?.unitBased?.[index]?.unitPrice}
              errorMessage={errors?.unitBased?.[index]?.unitPrice?.message}
              type='number'
              step={0.01}
              suffix={
                entityCurrency ? <span>{t(entityCurrency)}</span> : undefined
              }
            />
          </div>
        </div>
      </div>
    </EditableExpandableField>
  );
};

export default UnitInput;
